// Vi Vault Breaker (Q) — Vi charges up, then rockets into the target with a pink fist.
// Phases: WINDUP (energy builds at Vi) → DASH (Vi slides to target) → IMPACT (punch burst + shockwave) → FADE

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";
import type { UnitVisual } from "../UnitVisual";

const WINDUP_MS = 220;
const DASH_MS   = 260;
const IMPACT_MS = 200;
const FADE_MS   = 180;
const TOTAL     = WINDUP_MS + DASH_MS + IMPACT_MS + FADE_MS;
const IMPACT_START = WINDUP_MS + DASH_MS;

const SPARK_COUNT = 12;
const SPARKS = Array.from({ length: SPARK_COUNT }, (_, i) => ({
  angle: (i / SPARK_COUNT) * Math.PI * 2,
  len:   14 + (i % 3) * 9,
}));

function hexPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, rot: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = rot + (i / 6) * Math.PI * 2;
    if (i === 0) ctx.moveTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    else ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  ctx.closePath();
}

export class ViDashVFX implements Animation {
  private elapsed = 0;
  private impactFired = false;
  private angle: number;
  onEnd?: () => void;

  constructor(
    private visual: UnitVisual,
    private startPx: Point,
    private targetPx: Point,
    private hexSize: number,
    private onImpact: () => void,
  ) {
    this.angle = Math.atan2(targetPx.y - startPx.y, targetPx.x - startPx.x);
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);

    const dashElapsed = this.elapsed - WINDUP_MS;
    if (dashElapsed > 0 && dashElapsed < DASH_MS) {
      const t = Easing.easeInCubic(dashElapsed / DASH_MS);
      this.visual.x = this.startPx.x + (this.targetPx.x - this.startPx.x) * t;
      this.visual.y = this.startPx.y + (this.targetPx.y - this.startPx.y) * t;
    } else if (dashElapsed >= DASH_MS) {
      this.visual.x = this.targetPx.x;
      this.visual.y = this.targetPx.y;
    }

    if (!this.impactFired && this.elapsed >= IMPACT_START) {
      this.impactFired = true;
      this.onImpact();
    }

    if (this.elapsed >= TOTAL) this.onEnd?.();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const e = this.elapsed;
    if (e < WINDUP_MS) {
      this.drawWindup(ctx, e / WINDUP_MS);
    } else if (e < IMPACT_START) {
      this.drawDash(ctx, (e - WINDUP_MS) / DASH_MS);
    } else {
      this.drawImpact(ctx, (e - IMPACT_START) / (IMPACT_MS + FADE_MS));
    }
  }

  private drawWindup(ctx: CanvasRenderingContext2D, t: number): void {
    const x = this.startPx.x;
    const y = this.startPx.y;
    const pulse = 0.6 + 0.4 * Math.sin(t * Math.PI * 6);
    const r = this.hexSize * 0.35 + t * this.hexSize * 0.55 * pulse;

    ctx.save();

    // Outer hex glow
    ctx.globalAlpha = t * 0.6 * pulse;
    ctx.strokeStyle = "#ff44cc";
    ctx.lineWidth = 3;
    ctx.shadowColor = "#ff00aa";
    ctx.shadowBlur = 24;
    hexPath(ctx, x, y, r * 1.7, t * 1.8);
    ctx.stroke();

    // Inner counter-rotating hex
    ctx.globalAlpha = t * 0.8;
    ctx.strokeStyle = "#ffaaee";
    ctx.lineWidth = 2;
    ctx.shadowBlur = 12;
    hexPath(ctx, x, y, r, -t * 2.5);
    ctx.stroke();

    // Energy core
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r * 0.9);
    grad.addColorStop(0, "#ffffff");
    grad.addColorStop(0.4, "#ff88ee");
    grad.addColorStop(1, "rgba(200,0,160,0)");
    ctx.globalAlpha = t * 0.9;
    ctx.fillStyle = grad;
    ctx.shadowColor = "#ff44cc";
    ctx.shadowBlur = 20;
    ctx.beginPath();
    ctx.arc(x, y, r * 0.9, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawDash(ctx: CanvasRenderingContext2D, t: number): void {
    const curX = this.visual.x;
    const curY = this.visual.y;
    const cos = Math.cos(this.angle);
    const sin = Math.sin(this.angle);
    const trailLen = Math.hypot(curX - this.startPx.x, curY - this.startPx.y);

    ctx.save();

    // Wide energy trail from start to current position
    if (trailLen > 4) {
      ctx.translate(this.startPx.x, this.startPx.y);
      ctx.rotate(this.angle);

      const fade = 1 - t * 0.5;

      // Outer glow ribbon
      ctx.globalAlpha = fade * 0.45;
      ctx.fillStyle = "#ff00aa";
      ctx.shadowColor = "#ff44cc";
      ctx.shadowBlur = 22;
      ctx.fillRect(0, -this.hexSize * 0.52, trailLen, this.hexSize * 1.04);

      // Bright core streak
      ctx.globalAlpha = fade * 0.85;
      ctx.fillStyle = "#ffaaee";
      ctx.shadowBlur = 8;
      ctx.fillRect(0, -this.hexSize * 0.15, trailLen, this.hexSize * 0.3);

      // Hot white center line
      ctx.globalAlpha = fade;
      ctx.fillStyle = "#ffffff";
      ctx.shadowColor = "#ff88ee";
      ctx.shadowBlur = 6;
      ctx.fillRect(0, -2, trailLen, 4);

      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    // Fist glow at Vi's current position
    const fistGrad = ctx.createRadialGradient(curX - 2, curY - 2, 0, curX, curY, this.hexSize * 0.55);
    fistGrad.addColorStop(0, "#ffffff");
    fistGrad.addColorStop(0.3, "#ff88ee");
    fistGrad.addColorStop(0.7, "#dd00aa");
    fistGrad.addColorStop(1, "rgba(180,0,120,0)");
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = fistGrad;
    ctx.shadowColor = "#ff44cc";
    ctx.shadowBlur = 28;
    ctx.beginPath();
    ctx.arc(curX, curY, this.hexSize * 0.55, 0, Math.PI * 2);
    ctx.fill();

    // Rotating hex on fist
    ctx.globalAlpha = 0.55;
    ctx.strokeStyle = "#ffddff";
    ctx.lineWidth = 1.5;
    ctx.shadowBlur = 8;
    hexPath(ctx, curX, curY, this.hexSize * 0.38, t * 4.2);
    ctx.stroke();

    // Speed lines perpendicular to dash direction (above and below)
    ctx.globalAlpha = (1 - t) * 0.6;
    ctx.strokeStyle = "#ff44cc";
    ctx.lineWidth = 1.5;
    ctx.shadowColor = "#ff00aa";
    ctx.shadowBlur = 10;
    for (let i = -2; i <= 2; i++) {
      const lineLen = (18 + Math.abs(i) * 8) * (1 - t * 0.6);
      const ox = curX - cos * 10 - sin * i * 12;
      const oy = curY - sin * 10 + cos * i * 12;
      ctx.beginPath();
      ctx.moveTo(ox, oy);
      ctx.lineTo(ox - cos * lineLen, oy - sin * lineLen);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D, t: number): void {
    const fade = 1 - Easing.easeInQuad(t);
    const x = this.targetPx.x;
    const y = this.targetPx.y;

    ctx.save();

    // Initial white flash
    if (t < 0.2) {
      const flash = 1 - t / 0.2;
      ctx.globalAlpha = flash * 0.8;
      ctx.fillStyle = "#ffffff";
      ctx.shadowColor = "#ff88ee";
      ctx.shadowBlur = 30;
      ctx.beginPath();
      ctx.arc(x, y, this.hexSize * 0.9 * flash, 0, Math.PI * 2);
      ctx.fill();
    }

    // Primary shockwave ring
    const ring1 = this.hexSize * 0.4 + t * this.hexSize * 2.6;
    ctx.globalAlpha = fade * 0.9;
    ctx.strokeStyle = "#ff44cc";
    ctx.lineWidth = 4 - t * 2.5;
    ctx.shadowColor = "#ff00aa";
    ctx.shadowBlur = 24;
    ctx.beginPath();
    ctx.arc(x, y, ring1, 0, Math.PI * 2);
    ctx.stroke();

    // Secondary ring
    const ring2 = this.hexSize * 0.25 + t * this.hexSize * 1.6;
    ctx.globalAlpha = fade * 0.6;
    ctx.strokeStyle = "#ffaaee";
    ctx.lineWidth = 2;
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ctx.arc(x, y, ring2, 0, Math.PI * 2);
    ctx.stroke();

    // Hex burst
    ctx.globalAlpha = fade * 0.7;
    ctx.strokeStyle = "#ffddff";
    ctx.lineWidth = 1.5;
    ctx.shadowBlur = 10;
    hexPath(ctx, x, y, this.hexSize * 0.3 + t * this.hexSize * 2.0, t * 1.8);
    ctx.stroke();

    // Spark rays
    ctx.globalAlpha = fade * 0.85;
    ctx.strokeStyle = "#ff88ee";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#ff44cc";
    ctx.shadowBlur = 10;
    for (const s of SPARKS) {
      const inner = this.hexSize * 0.2 + t * this.hexSize * 0.4;
      const outer = inner + s.len * Easing.easeOutCubic(t);
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(s.angle) * inner, y + Math.sin(s.angle) * inner);
      ctx.lineTo(x + Math.cos(s.angle) * outer, y + Math.sin(s.angle) * outer);
      ctx.stroke();
    }

    // Directional punch shockwave — elongated ellipse in dash direction
    if (t < 0.5) {
      const et = t / 0.5;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(this.angle);
      ctx.globalAlpha = (1 - et) * 0.45;
      ctx.strokeStyle = "#ff44cc";
      ctx.lineWidth = 3;
      ctx.shadowColor = "#ff00aa";
      ctx.shadowBlur = 18;
      ctx.beginPath();
      ctx.ellipse(this.hexSize * 0.4 * et, 0, this.hexSize * 1.4 * et, this.hexSize * 0.55 * et, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
